import React, { useMemo, useState } from 'react'
import { ActivityIndicator, FlatList, Platform, StyleSheet, TouchableOpacity, View } from 'react-native'
import { useTheme } from '@react-navigation/native'
import type { NativeStackScreenProps } from '@react-navigation/native-stack'
import { Header, Icon, Screen, SearchBar, Text } from '@components'
import { useLanguage } from '@context'
import { Theme } from '@theme'
import { eventAccessProblem, foldForSearch } from '@utils'
import {
  type OrganizerAttendee,
  useCheckInAttendees,
  useEventById,
  useLiveCheckIn,
} from '@hooks'
import type { EventsStackParamList } from '../../../navigation/navigator/EventsStackNavigator'
import { AccessDenied } from '../components/AccessDenied'
import { EventAvatar } from '../../events/components/EventAvatar'
import { OfflineBanner } from './components/OfflineBanner'
import { useOfflineCheckIn } from './offlineCheckIn/useOfflineCheckIn'

type Props = NativeStackScreenProps<EventsStackParamList, 'EventCheckIn'>

const byName = (a: OrganizerAttendee, b: OrganizerAttendee) =>
  `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`, undefined, {
    sensitivity: 'base',
  })

/**
 * "Accueil": the entrance screen, made for a phone held at the door. Live
 * counter, instant accent-insensitive search, one big Présent / Absent choice
 * per person. Optional: the CRM keeps its own check-in too.
 */
export const EventCheckIn: React.FC<Props> = ({ route, navigation }) => {
  const theme = useTheme()
  const { t } = useLanguage()
  const styles = getStyles(theme)
  const { eventId } = route.params
  const { data: detail, error: detailError } = useEventById(eventId)
  const { data: liveAttendees, isLoading, isError: attendeesUnreachable } = useCheckInAttendees(eventId)
  const checkIn = useLiveCheckIn(eventId)
  const offline = useOfflineCheckIn(eventId)
  const [search, setSearch] = useState('')

  // Offline with no live data yet (e.g. straight after a hard reload): fall
  // back to the manifest downloaded when this screen last opened, so the
  // list and the counter are never just empty.
  const fallbackAttendees = useMemo<OrganizerAttendee[]>(
    () =>
      offline.manifest.map(
        (ticket): OrganizerAttendee => ({
          attendeeId: ticket.attendeeId,
          accountId: ticket.accountId,
          email: '',
          firstName: ticket.firstName,
          lastName: ticket.lastName,
          jobTitle: null,
          company: null,
          avatarUrl: null,
          linkedinUrl: null,
          source: 'account',
          joinedAt: new Date(0).toISOString(),
          checkedInAt: ticket.checkedInAt,
          payment: null,
        }),
      ),
    [offline.manifest],
  )
  const attendees: OrganizerAttendee[] | undefined =
    liveAttendees ?? (attendeesUnreachable ? fallbackAttendees : undefined)

  const total = attendees?.length ?? 0
  // "à jour y compris hors ligne": present here means the server's own
  // checked-in flag, OR a scan still sitting in the local offline queue.
  const present =
    attendees?.filter((a) => a.checkedInAt || (a.attendeeId && offline.presentAttendeeIds.has(a.attendeeId))).length ??
    0

  // Absent first (they still have to be welcomed), alphabetical; then present.
  const rows = useMemo(() => {
    const needle = foldForSearch(search)
    const matching = (attendees ?? []).filter(
      (a) =>
        !needle ||
        foldForSearch(`${a.firstName} ${a.lastName} ${a.email}`).includes(needle),
    )
    const isPresent = (a: OrganizerAttendee) =>
      Boolean(a.checkedInAt) || (!!a.attendeeId && offline.presentAttendeeIds.has(a.attendeeId))
    return [...matching.filter((a) => !isPresent(a)).sort(byName), ...matching.filter(isPresent).sort(byName)]
  }, [attendees, search, offline.presentAttendeeIds])

  const accessProblem = eventAccessProblem(detail, detailError)
  if (accessProblem) {
    return (
      <AccessDenied
        message={t(`events.access.${accessProblem === 'notFound' ? 'notFound' : 'forbidden'}`)}
        onBack={() => navigation.navigate('EventsList')}
      />
    )
  }

  return (
    <Screen>
      <Header
        title={t('events.checkin.title')}
        iconLeft={Platform.OS === 'web' ? 'arrowLeft' : 'arrowBack'}
        onPressLeft={() => navigation.navigate('EventManage', { eventId })}
        iconRight="scan"
        onPressRight={() => navigation.navigate('EventCheckInScan', { eventId })}
      />
      <View style={styles.page}>
        <View style={styles.top}>
          <OfflineBanner isOnline={offline.isOnline} pendingCount={offline.pendingCount} />
          {detail ? (
            <Text type="bodySmall" color={theme.colors.greyBlue} numberOfLines={1}>
              {detail.event.name}
            </Text>
          ) : null}
          <View style={styles.counter} testID="checkin-counter" accessibilityLiveRegion="polite">
            <Text type="titleBigBold" color={theme.colors.green}>
              {present}
            </Text>
            <Text type="body" color={theme.colors.secondary} style={styles.counterText}>
              {t('events.checkin.counter', { present, total })}
            </Text>
          </View>
          <SearchBar
            testID="checkin-search"
            value={search}
            onChangeText={setSearch}
            containerStyle={styles.search}
          />
        </View>

        {isLoading ? (
          <ActivityIndicator color={theme.colors.primary} />
        ) : (
          <FlatList
            data={rows}
            keyExtractor={(item) => item.attendeeId ?? item.accountId}
            contentContainerStyle={styles.list}
            keyboardShouldPersistTaps="handled"
            ListEmptyComponent={
              <Text type="body" color={theme.colors.greyBlue} center style={styles.empty}>
                {search ? t('events.checkin.noResult') : t('events.checkin.empty')}
              </Text>
            }
            renderItem={({ item }) => (
              <CheckInRow
                attendee={item}
                present={Boolean(item.checkedInAt) || (!!item.attendeeId && offline.presentAttendeeIds.has(item.attendeeId))}
                onChange={(checkedIn) =>
                  item.attendeeId && checkIn.mutate({ attendeeId: item.attendeeId, checkedIn })
                }
              />
            )}
          />
        )}
      </View>
    </Screen>
  )
}

interface CheckInRowProps {
  attendee: OrganizerAttendee
  /** Server-confirmed OR still sitting in the local offline queue. */
  present: boolean
  onChange: (checkedIn: boolean) => void
}

function CheckInRow({ attendee, present, onChange }: CheckInRowProps) {
  const theme = useTheme()
  const { t } = useLanguage()
  const styles = getStyles(theme)
  const role = [attendee.jobTitle, attendee.company].filter(Boolean).join(' · ')

  return (
    <View style={[styles.row, present && styles.rowPresent]} testID={`checkin-row-${attendee.attendeeId}`}>
      <View style={styles.identityLine}>
        <EventAvatar
          firstName={attendee.firstName}
          lastName={attendee.lastName}
          avatarUrl={attendee.avatarUrl}
          size={48}
        />
        <View style={styles.identity}>
          <Text type="bodyBold" numberOfLines={1}>
            {`${attendee.firstName} ${attendee.lastName}`}
          </Text>
          {role ? (
            <Text type="bodySmall" color={theme.colors.secondary} numberOfLines={1}>
              {role}
            </Text>
          ) : null}
        </View>
      </View>
      <View style={styles.choice}>
        <TouchableOpacity
          accessibilityRole="button"
          accessibilityState={{ selected: present }}
          aria-selected={present}
          testID={`checkin-present-${attendee.attendeeId}`}
          onPress={() => !present && onChange(true)}
          style={[styles.choiceButton, present && styles.choicePresentOn]}
        >
          <Icon name="check" size={18} color={present ? theme.colors.white : theme.colors.greyBlue} />
          <Text
            type="bodyBold"
            color={present ? theme.colors.white : theme.colors.secondary}
            style={styles.choiceLabel}
          >
            {t('events.checkin.present')}
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          accessibilityRole="button"
          accessibilityState={{ selected: !present }}
          aria-selected={!present}
          testID={`checkin-absent-${attendee.attendeeId}`}
          onPress={() => present && onChange(false)}
          style={[styles.choiceButton, !present && styles.choiceAbsentOn]}
        >
          <Text type="bodyBold" color={!present ? theme.colors.white : theme.colors.secondary}>
            {t('events.checkin.absent')}
          </Text>
        </TouchableOpacity>
      </View>
    </View>
  )
}

const TOUCH_TARGET = 48

const getStyles = (theme: Theme) =>
  StyleSheet.create({
    page: { flex: 1, width: '100%', maxWidth: 720, alignSelf: 'center' },
    top: {
      paddingHorizontal: theme.spacing.lg,
      paddingBottom: theme.spacing.md,
      gap: theme.spacing.sm,
    },
    counter: { flexDirection: 'row', alignItems: 'baseline', flexWrap: 'wrap' },
    counterText: { marginLeft: theme.spacing.sm, flexShrink: 1 },
    search: { width: '100%' },
    list: { paddingHorizontal: theme.spacing.lg, paddingBottom: theme.spacing.xxxl, gap: theme.spacing.sm },
    empty: { paddingVertical: theme.spacing.xxl },
    row: {
      padding: theme.spacing.md,
      borderRadius: theme.border.large,
      borderWidth: 1,
      borderColor: theme.colors.grey,
      backgroundColor: theme.colors.white,
      gap: theme.spacing.md,
    },
    rowPresent: { backgroundColor: theme.colors.successSoft },
    identityLine: { flexDirection: 'row', alignItems: 'center', gap: theme.spacing.md },
    identity: { flex: 1, minWidth: 0, gap: 1 },
    choice: { flexDirection: 'row', gap: theme.spacing.sm },
    choiceButton: {
      flex: 1,
      minHeight: TOUCH_TARGET,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: theme.border.jumbo,
      borderWidth: 1,
      borderColor: theme.colors.grey,
      backgroundColor: theme.colors.white,
    },
    choicePresentOn: { backgroundColor: theme.colors.green, borderColor: theme.colors.green },
    choiceAbsentOn: { backgroundColor: theme.colors.greyBlue, borderColor: theme.colors.greyBlue },
    choiceLabel: { marginLeft: theme.spacing.xs },
  })
